export type SummaryEntry = { variantId: string; quantity: number };
export type SummaryAddOn = { id: string; name: string };
export type SummaryVariant = {
  id: string;
  name: string;
  addOns?: readonly SummaryAddOn[];
};

export type TicketSummaryInclude = 'tickets' | 'addons' | 'all';

/**
 * Build a human-readable summary of selected tickets and/or add-ons, e.g.
 * "2 Adult, 1 Child" or "2 Adult, 1 Lunch". Entries with quantity 0 or an
 * unknown id are skipped. `include` selects which kinds to render; by
 * default only ticket variants are included.
 */
export function buildTicketSummary(
  tickets: readonly SummaryEntry[],
  variants: readonly SummaryVariant[],
  options: { include?: TicketSummaryInclude } = {}
): string {
  const { include = 'tickets' } = options;

  const names = new Map<string, { name: string; kind: 'ticket' | 'addon' }>();
  for (const variant of variants) {
    names.set(variant.id, { name: variant.name, kind: 'ticket' });
    for (const addOn of variant.addOns ?? []) {
      names.set(addOn.id, { name: addOn.name, kind: 'addon' });
    }
  }

  return tickets
    .filter((t) => t.quantity > 0)
    .map((t) => ({ ticket: t, entry: names.get(t.variantId) }))
    .filter(({ entry }) => {
      if (!entry) return false;
      if (include === 'all') return true;
      return include === 'tickets'
        ? entry.kind === 'ticket'
        : entry.kind === 'addon';
    })
    .map(({ ticket, entry }) => `${ticket.quantity} ${entry!.name}`)
    .join(', ');
}
